import React from 'react';
import { Link } from 'wouter';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Separator } from "@/components/ui/separator";
import TypeformWidget from '@/components/ui/TypeformWidget';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';

const Mentorship: React.FC = () => {
  const scrollToTypeform = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault();
    const typeformSection = document.getElementById('mentorship-application-form');
    if (typeformSection) {
      typeformSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="flex flex-col min-h-screen">
      <Navbar />
      <main className="flex-grow">
        {/* Hero Section */}
        <section className="bg-indigo-950 text-white py-16 lg:py-24 px-4">
          <div className="max-w-5xl mx-auto">
            <div className="max-w-3xl">
              <h1 className="text-3xl md:text-4xl font-serif font-medium mb-6 leading-tight tracking-tight">
                Build the Future of Health—With Guidance That's Ahead of the Curve
              </h1>
              <p className="text-base md:text-lg mb-8 text-gray-200 leading-relaxed max-w-2xl">
                A mentorship for clinicians, entrepreneurs, and biohackers shaping next-generation longevity care.
              </p>
              <Button 
                onClick={scrollToTypeform}
                className="bg-white text-indigo-950 hover:bg-gray-100 text-sm px-6 py-2 rounded-sm">
                Apply Now
              </Button>
            </div>
          </div>
        </section>

        {/* The Invitation Section */}
        <section className="py-12 px-4 bg-white">
          <div className="max-w-4xl mx-auto">
            <p className="text-lg md:text-xl font-serif text-gray-800 leading-relaxed">
              You're not just following trends. You're building what comes next. This mentorship pairs deep clinical insight with real-world operating know-how—plus a network of peers building with intention, not hype.
            </p>
          </div>
        </section>

        {/* What You Get Section */}
        <section className="py-16 px-4 bg-gray-50">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl font-serif font-medium text-gray-900 mb-2">What You Get</h2>
            <Separator className="mb-8 bg-indigo-900/30" />
            
            <div className="grid md:grid-cols-2 gap-6">
              <Card className="border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                <CardContent className="p-6">
                  <h3 className="text-base font-semibold mb-3 text-indigo-900">Continuously Updated Curriculum</h3>
                  <p className="text-sm text-gray-700 leading-relaxed">
                    Access a living library of modules covering the foundational and emerging science of longevity, cellular health, and personalized medicine—alongside tactical business guides on marketing, operations, and patient retention. Whether you're running a clinic or launching a startup, this curriculum gives you frameworks that work in the real world.
                  </p>
                </CardContent>
              </Card>

              <Card className="border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                <CardContent className="p-6">
                  <h3 className="text-base font-semibold mb-3 text-indigo-900">Private WhatsApp Community</h3>
                  <p className="text-sm text-gray-700 leading-relaxed">
                    Join a curated network of mission-aligned clinicians, founders, and builders. This is not another noisy group chat—it's a space where real conversations happen, referrals are shared, bottlenecks get unblocked, and ideas are pressure-tested.
                  </p>
                </CardContent>
              </Card>

              <Card className="border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                <CardContent className="p-6">
                  <h3 className="text-base font-semibold mb-3 text-indigo-900">Monthly Live Q&A Sessions</h3>
                  <p className="text-sm text-gray-700 leading-relaxed">
                    Each month, we meet to go deep: dissect clinical cases, troubleshoot business roadblocks, and spotlight what's new in the field. Think of these as your monthly pulse check on what matters most in longevity medicine.
                  </p>
                </CardContent>
              </Card>

              <Card className="border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                <CardContent className="p-6">
                  <h3 className="text-base font-semibold mb-3 text-indigo-900">Discounted Software & Digital Tools</h3>
                  <p className="text-sm text-gray-700 leading-relaxed">
                    Unlock member-only pricing on tools that streamline personalized protocol design, lab interpretation, supplement retail, and digital patient engagement. Whether you're bootstrapped or scaling, these tools give you leverage.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Who It's For Section */}
        <section className="py-16 px-4 bg-white">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl font-serif font-medium text-gray-900 mb-2">Who It's For</h2>
            <Separator className="mb-8 bg-indigo-900/30" />
            
            <div className="grid md:grid-cols-3 gap-6">
              <Card className="border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                <CardContent className="p-6">
                  <h3 className="text-base font-semibold mb-3 text-indigo-900">Biohackers with Vision</h3>
                  <p className="text-sm text-gray-700 leading-relaxed">
                    You've spent years self-experimenting, reading the literature, tracking every molecule and marker. Now you're ready to turn your curiosity into impact—building a brand, launching a product, or guiding others through what you've learned.
                  </p>
                </CardContent>
              </Card>

              <Card className="border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                <CardContent className="p-6">
                  <h3 className="text-base font-semibold mb-3 text-indigo-900">Entrepreneurs in Health Innovation</h3>
                  <p className="text-sm text-gray-700 leading-relaxed">
                    Whether you're designing software, launching a supplement line, or building a hybrid clinic, you need more than marketing. This mentorship brings the clinical depth and operator insight to help you build something with staying power.
                  </p>
                </CardContent>
              </Card>

              <Card className="border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                <CardContent className="p-6">
                  <h3 className="text-base font-semibold mb-3 text-indigo-900">Clinicians at the Edge of Medicine</h3>
                  <p className="text-sm text-gray-700 leading-relaxed">
                    You already practice functional or integrative care—but you want to go deeper. You're done with prefab protocols and want to understand the mechanisms, deploy advanced diagnostics, and design interventions that are truly personalized.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Why This Program Is Different Section */}
        <section className="py-16 px-4 bg-indigo-950 text-white">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl font-serif font-medium mb-6">Why This Program Is Different</h2>
            <p className="text-sm leading-relaxed">
              Most longevity content is superficial. This mentorship is different: grounded in deep science, shaped by real operating experience, and curated for people actually building. No fluff. No filler. Just the frameworks, knowledge, and tools that move the needle.
            </p>
          </div>
        </section>

        {/* Pricing & Commitment Section */}
        <section className="py-16 px-4 bg-white">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl font-serif font-medium text-gray-900 mb-2">Pricing & Commitment</h2>
            <Separator className="mb-8 bg-indigo-900/30" />
            
            <div className="max-w-xl">
              <div className="mb-8">
                <p className="text-lg font-semibold text-indigo-900 mb-2">Membership · $5,000 per year</p>
                <p className="text-sm text-gray-600">(billed annually)</p>
                <p className="mt-4 text-sm text-gray-700">No upsells. No commissions. Just serious mentorship and a network designed to accelerate your trajectory.</p>
              </div>
              <Button 
                onClick={scrollToTypeform}
                className="bg-indigo-950 hover:bg-indigo-900 text-white text-sm px-6 py-2 rounded-sm">
                Apply to Join →
              </Button>
            </div>
          </div>
        </section>

        {/* Application Section */}
        <section id="mentorship-application-form" className="py-16 px-4 bg-gray-50">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl font-serif font-medium text-gray-900 mb-2">Let's Build Something That Matters</h2>
            <Separator className="mb-8 bg-indigo-900/30" />
            <p className="text-sm text-gray-700 mb-8">Apply to join the mentorship program</p>
            
            <div className="max-w-2xl">
              {/* Typeform Embed */}
              <div className="w-full">
                <TypeformWidget formId="01JTP5J0TCN6AKSX0ZKCNJ0MXW" />
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16 px-4 bg-white">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl font-serif font-medium text-gray-900 mb-2">Frequently Asked Questions</h2>
            <Separator className="mb-8 bg-indigo-900/30" />
            
            <div className="max-w-2xl">
              <Accordion type="single" collapsible className="space-y-2">
                <AccordionItem value="item-1" className="border border-gray-200 rounded-sm">
                  <AccordionTrigger className="text-sm font-medium text-gray-900 px-4 py-3 hover:bg-gray-50">
                    Is the fee annual or one-time?
                  </AccordionTrigger>
                  <AccordionContent className="text-xs text-gray-700 px-4 pb-4">
                    The mentorship is an annual membership at <strong>$5,000 per year</strong>, billed upfront.
                  </AccordionContent>
                </AccordionItem>
                
                <AccordionItem value="item-2" className="border border-gray-200 rounded-sm">
                  <AccordionTrigger className="text-sm font-medium text-gray-900 px-4 py-3 hover:bg-gray-50">
                    What if I'm just getting started?
                  </AccordionTrigger>
                  <AccordionContent className="text-xs text-gray-700 px-4 pb-4">
                    You don't need to be established. The program is designed to meet you where you are—whether you're validating an idea, treating patients, or scaling a business. You'll build foundational fluency and grow from there.
                  </AccordionContent>
                </AccordionItem>
                
                <AccordionItem value="item-3" className="border border-gray-200 rounded-sm">
                  <AccordionTrigger className="text-sm font-medium text-gray-900 px-4 py-3 hover:bg-gray-50">
                    How much time should I set aside each month?
                  </AccordionTrigger>
                  <AccordionContent className="text-xs text-gray-700 px-4 pb-4">
                    Expect 2–3 hours per week: about 90 minutes of module time, one live Q&A per month, and flexible engagement with the WhatsApp group. You can move at your own pace—everything is recorded and organized for on-demand access.
                  </AccordionContent>
                </AccordionItem>
                
                <AccordionItem value="item-4" className="border border-gray-200 rounded-sm">
                  <AccordionTrigger className="text-sm font-medium text-gray-900 px-4 py-3 hover:bg-gray-50">
                    Can I expense this through my business?
                  </AccordionTrigger>
                  <AccordionContent className="text-xs text-gray-700 px-4 pb-4">
                    Yes. Most members categorize this as continuing education, business development, or professional training. You'll receive an itemized invoice upon payment.
                  </AccordionContent>
                </AccordionItem>
                
                <AccordionItem value="item-5" className="border border-gray-200 rounded-sm">
                  <AccordionTrigger className="text-sm font-medium text-gray-900 px-4 py-3 hover:bg-gray-50">
                    What's the refund or cancellation policy?
                  </AccordionTrigger>
                  <AccordionContent className="text-xs text-gray-700 px-4 pb-4">
                    You'll have a 10-day grace period after enrolling to explore the platform and community. If it's not the right fit, request a full refund within that window. After that, you'll retain access to all content for the duration of your membership year. No auto-renewal—you choose whether to continue.
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Mentorship;